import { HStack, Text, Tooltip } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  ModelLockLockableTypeEnum,
  type SortOrder,
  useBulkDownloadProformaInvoicesLazyQuery,
  useSendProformaBulkMutation,
} from "@src/__generated__/graphql";
import { OutgoingProformaInvoiceOrderByColumnEnum } from "@src/__generated__/urql-graphql";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  Link,
  ModalConfirm,
  TruncatedTextWithTooltip,
} from "@src/components/ui-kit";
import { LockableColumn } from "@src/components/ui-kit/DataTable/columns/lockable-column";
import { Icon } from "@src/components/ui-kit/Icon";
import type { BulkAction } from "@src/components/widgets/bulk-actions/types";
import { appStore } from "@src/stores/AppStore";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import { currency, formatDate } from "../../../../../../utils/formatters";
import type { ProformaInvoice } from "../../Models.types";
import { useStore } from "../../store";
import { Column } from "./Column";

type ProformaInvoiceRow = ProformaInvoice & Record<string, unknown>;

export const Table = observer(function Table() {
  const store = useStore();
  const [idsToResend, setIdsToResend] = useState<string[]>([]);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  const [sendProformaBulkMutation, sendProformaBulkState] =
    useSendProformaBulkMutation({
      ...onError(),
      onCompleted() {
        setIsResendModalOpen(false);
        store.proformaInvoicesRefetch?.();
        store.setSelectedIds([]);
      },
    });

  const [downloadProformas, downloadProformasState] =
    useBulkDownloadProformaInvoicesLazyQuery({
      onCompleted(data) {
        if (data.bulkDownloadOutgoingProformaInvoices) {
          window.location.href = data.bulkDownloadOutgoingProformaInvoices;
        }
      },
      ...onError(),
    });

  const bulkActions = useMemo<BulkAction[]>(
    () => [
      {
        id: "proforma-actions",
        label: () => t`Actions`,
        icon: "dots-vertical",
        isLoading: () =>
          sendProformaBulkState.loading ||
          downloadProformasState.loading ||
          store.isBulkLockToggling,
        subActions: [
          ...(can("outgoingInvoice_sendToClient")
            ? [
                {
                  id: "resend-invoice",
                  label: () => t`Resend invoice`,
                  callback: (selectedIds: string[] | Set<string>) => {
                    setIdsToResend(Array.from(selectedIds));
                    setIsResendModalOpen(true);
                  },
                },
              ]
            : []),
          ...(can("outgoingInvoice_modelLock")
            ? [
                {
                  id: "lock",
                  label: () => t`Lock`,
                  callback: (selectedIds: string[] | Set<string>) => {
                    store.bulkLock(Array.from(selectedIds));
                  },
                },
                {
                  id: "unlock",
                  label: () => t`Unlock`,
                  callback: (selectedIds: string[] | Set<string>) => {
                    store.bulkUnlock(Array.from(selectedIds));
                  },
                },
              ]
            : []),
          {
            id: "download-pdf",
            label: () => t`Download PDF`,
            callback: (selectedIds: string[] | Set<string>) => {
              downloadProformas({
                variables: { ids: Array.from(selectedIds) },
              });
            },
          },
        ],
      },
    ],
    [
      downloadProformas,
      downloadProformasState.loading,
      sendProformaBulkState.loading,
    ],
  );
  const cols = useMemo<
    BodyColumnDef<
      ProformaInvoiceRow,
      OutgoingProformaInvoiceOrderByColumnEnum
    >[]
  >(() => {
    const columns: BodyColumnDef<
      ProformaInvoiceRow,
      OutgoingProformaInvoiceOrderByColumnEnum
    >[] = [
      {
        id: "invoice_no",
        meta: { label: t`Proforma no` },
        header: t`Proforma no`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.InvoiceNo,
        cell: ({ row }) => <Column.ProformaNo row={row.original} />,
      },
      {
        id: "brands",
        meta: { label: t`Brands` },
        header: t`Brands`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.BrandName,
        cell: ({ row }) => <Column.Brand row={row.original} />,
      },
      {
        id: "projects",
        meta: { label: t`Project/s` },
        header: t`Project/s`,
        cell: ({ row }) => <Column.Project row={row.original} />,
      },
      {
        id: "unusedAmount",
        meta: { label: t`Unused amount` },
        header: t`Unused amount`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.UnusedAmount,
        cell: ({ row }) =>
          currency.formatByCurrency(
            row.original.stats.unused_amount_in_currency,
            row.original.currency,
          ),
      },
      {
        id: "amount",
        meta: { label: t`Amount` },
        header: t`Amount`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.Amount,
        cell: ({ row }) => <Column.Amount row={row.original} />,
      },
      {
        id: "amount_with_vat",
        meta: { label: t`Amount (VAT)` },
        header: t`Amount (VAT)`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.AmountWithVat,
        cell: ({ row }) => <Column.AmountWithVat row={row.original} />,
      },
      {
        id: "issue_date",
        meta: { label: t`Issue date` },
        header: t`Issue date`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.IssueDate,
        cell: ({ row }) => <Column.IssueDate row={row.original} />,
      },
      {
        id: "due_date",
        meta: { label: t`Due date` },
        header: t`Due date`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.DueDate,
        cell: ({ row }) => <Column.DueDate row={row.original} />,
      },
      {
        id: "submitted_at",
        meta: { label: t`Submitted at` },
        header: t`Submitted at`,
        cell: ({ row }) =>
          row.original.request_for_invoicing_submit_date
            ? formatDate(
                new Date(row.original.request_for_invoicing_submit_date),
              )
            : null,
      },
      {
        id: "created_at",
        meta: { label: t`Created at` },
        header: t`Created at`,
        cell: ({ row }) =>
          row.original.request_for_invoicing_created_date
            ? formatDate(
                new Date(row.original.request_for_invoicing_created_date),
              )
            : null,
      },
      {
        id: "paid",
        meta: { label: t`Paid` },
        header: t`Paid`,
        cell: ({ row }) =>
          row.original.paid
            ? currency.formatByCurrency(
                row.original.paid,
                row.original.currency,
              )
            : null,
      },
      {
        id: "paid_at",
        meta: { label: t`Paid at` },
        header: t`Paid at`,
        orderBy: OutgoingProformaInvoiceOrderByColumnEnum.PaymentStatus,
        cell: ({ row }) => <Column.PaidAt row={row.original} />,
      },
      {
        id: "documents",
        meta: { label: t`Proforma` },
        header: t`Proforma`,
        cell: ({ row }) => <Column.Documents row={row.original} />,
      },
      {
        id: "modelLock",
        header: () => "",
        meta: { label: t`Lock` },
        size: 40,
        cell: ({ row }) => (
          <LockableColumn
            modelLock={row.original.modelLock}
            canToggle={can("outgoingInvoice_modelLock")}
            onClick={(locked) =>
              locked
                ? store.unlockProformaInvoice(row.original.id)
                : store.lockProformaInvoice(row.original.id)
            }
            isLoading={appStore.modelLock.isLoading(
              row.original.id,
              ModelLockLockableTypeEnum.OutgoingProformaInvoice,
            )}
          />
        ),
      },
      {
        id: "final-invoice",
        meta: { label: t`Final invoice` },
        header: t`Final invoice`,
        cell: ({ row }) => (
          <HStack spacing="2">
            {row.original.summaryInvoices?.map((invoice) => (
              <Link
                key={invoice.id}
                href={{
                  pathname: "/invoices/outgoing-invoices/detail",
                  query: { id: invoice.id },
                }}
                target="_blank"
              >
                <Tooltip label={invoice.invoice_no} shouldWrapChildren>
                  <Icon name="file-06" color="green.500" />
                </Tooltip>
              </Link>
            ))}
          </HStack>
        ),
      },
      {
        id: "payment_method",
        meta: { label: t`Payment method` },
        header: t`Payment method`,
        cell: ({ row }) => row.original.invoicePaymentMethod?.name ?? "—",
      },
      {
        id: "note",
        meta: { label: t`Note` },
        header: t`Note`,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.note} />
        ),
      },
      {
        id: "internal_note",
        meta: { label: t`Internal note` },
        header: t`Internal note`,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.internal_note} />
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: t`Actions`,
        cell: ({ row }) => <Column.Actions row={row.original} />,
      },
    ];

    if (appStore.workspaceStore.hasMultipleCompanies()) {
      columns.splice(3, 0, {
        id: "legal-entity",
        meta: { label: t`Legal entity` },
        header: t`Legal entity`,
        enableHiding: false,
        cell: ({ row }) => (
          <Text>
            {appStore.workspaceStore.getCompany(
              row.original.workspace_company_id,
            )?.name ?? ""}
          </Text>
        ),
      });
    }

    return columns;
  }, [store]);

  return (
    <React.Fragment>
      <DataTable.Body
        tableKey="proforma-invoices"
        data={store.proformaInvoices as ProformaInvoiceRow[]}
        columns={cols}
        enableRowSelection
        bulkActions={bulkActions}
        orderedBy={store.orderBy.clauses[0]}
        onOrderByChange={(clause) => {
          if (!clause.order) {
            store.orderBy.setClauses([]);
            return;
          }

          store.orderBy.setClauses([
            { column: clause.column, order: clause.order as SortOrder },
          ]);
        }}
        selected={store.selectedInvoiceIds}
        onSelect={(ids) => store.setSelectedIds(ids)}
        allIds={store.proformaInvoicesSummary?.outgoingInvoiceIds || []}
      />
      <ModalConfirm
        isOpen={isResendModalOpen}
        onClose={() => setIsResendModalOpen(false)}
        onConfirm={() =>
          sendProformaBulkMutation({
            variables: { ids: idsToResend },
          })
        }
        isLoading={sendProformaBulkState.loading}
      />
    </React.Fragment>
  );
});
